import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import bgImg from "../../pnai-admin-bg.jpg";
import Logo from "../common/Logo";
import Button from "../common/Button";
import Input from "../common/Input";
import SalesCallActivity from "./SalesCallActivity";
import AgencyMetricsBody from "../AgencyManagement/AgencyMetricsBody";

const ADMIN_EMAIL_KEY = "agency-management-email";
const ADMIN_AUTH_KEY = "agency-management-auth";

type Tab = "calls" | "agencies";

/**
 * Combined internal sales portal (/sales-metrics): one loft303 login gate, then
 * tabs for Aircall call activity and agency acquisition metrics. Replaces the
 * former /sales-management and /agency-metrics routes.
 */
function SalesMetrics() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [tab, setTab] = useState<Tab>("calls");

  const handleLogout = useCallback(() => {
    localStorage.removeItem(ADMIN_AUTH_KEY);
    localStorage.removeItem(ADMIN_EMAIL_KEY);
    setIsAuthenticated(false);
    setAdminEmail("");
    setEmail("");
    setPassword("");
  }, []);

  // Restore an existing portal session on mount.
  useEffect(() => {
    const authStatus = localStorage.getItem(ADMIN_AUTH_KEY);
    const storedEmail = localStorage.getItem(ADMIN_EMAIL_KEY);
    if (authStatus === "true" && storedEmail) {
      setAdminEmail(storedEmail);
      setEmail(storedEmail);
      setIsAuthenticated(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError("");
    setAuthLoading(true);
    try {
      const res = await axios.post("/api/agency-management/verify-password", { email, password });
      if (res.data.success) {
        localStorage.setItem(ADMIN_AUTH_KEY, "true");
        localStorage.setItem(ADMIN_EMAIL_KEY, email);
        setAdminEmail(email);
        setIsAuthenticated(true);
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        setAuthError(err.response.data.message || "Invalid email or password");
      } else {
        setAuthError("An error occurred. Please try again.");
      }
    } finally {
      setAuthLoading(false);
    }
  };

  // --- login gate ---
  if (!isAuthenticated) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2">
        <div
          className="hidden md:flex relative items-center justify-center bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${bgImg})` }}
        >
          <div className="absolute inset-0 bg-blue-50/70 dark:bg-blue-900/70"></div>
          <div className="w-3/5 z-10">
            <Logo className="w-full h-auto" />
          </div>
        </div>
        <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-base-200">
          <div className="bg-base-100 p-8 rounded-xl shadow-md dark:border dark:border-base-300 w-full max-w-sm">
            <h1 className="text-3xl mb-4 font-semibold">Sales Metrics</h1>
            <p className="text-gray-600 dark:text-base-content/70 mb-6 text-sm">
              Enter your email and password to access the sales metrics portal
            </p>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <p className="font-semibold pb-1 text-sm">Email</p>
                <Input
                  type="email"
                  id="email"
                  value={email}
                  placeholder="Enter your email"
                  onChange={(e) => {
                    setAuthError("");
                    setEmail(e.target.value);
                  }}
                  className={`input w-full ${authError && "border-red-500"}`}
                />
              </div>
              <div>
                <p className="font-semibold pb-1 text-sm">Password</p>
                <Input
                  type="password"
                  id="password"
                  value={password}
                  placeholder="Enter password"
                  onChange={(e) => {
                    setAuthError("");
                    setPassword(e.target.value);
                  }}
                  className={`input w-full ${authError && "border-red-500"}`}
                />
                {authError && <p className="mt-1 text-xs text-red-500">{authError}</p>}
              </div>
              <Button className="w-full" variant="primary" type="submit" disabled={authLoading}>
                {authLoading ? "Verifying..." : "Access Portal"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // --- authenticated portal ---
  return (
    <div className="min-h-screen bg-base-200 p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header + tabs */}
        <div className="bg-base-100 rounded-xl shadow-md dark:border dark:border-base-300 p-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-base-content">Sales Metrics</h1>
              <p className="text-gray-600 dark:text-base-content/70 mt-1">Aircall call activity and agency acquisitions</p>
            </div>
            <Button onClick={handleLogout} variant="secondary" className="text-sm">
              Logout
            </Button>
          </div>

          <div className="mt-6 flex gap-2 border-b border-base-300 -mb-6">
            {([
              ["calls", "Call Activity"],
              ["agencies", "Agency Metrics"],
            ] as const).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  tab === key
                    ? "border-blue-600 text-blue-600 dark:text-blue-300"
                    : "border-transparent text-base-content/60 hover:text-gray-700 dark:hover:text-base-content/80"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {tab === "calls" ? (
          <SalesCallActivity adminEmail={adminEmail} onUnauthorized={handleLogout} />
        ) : (
          <AgencyMetricsBody adminEmail={adminEmail} onUnauthorized={handleLogout} />
        )}
      </div>
    </div>
  );
}

export default SalesMetrics;
